import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SOURCES, WEBSITE_STATES, PRIORITIES } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

export function EditLeadDialog({ open, onOpenChange, lead, onSaved }) {
  const [form, setForm] = useState({
    company: lead.company || "", industry: lead.industry || "", website: lead.website || "",
    contact_person: lead.contact_person || "", phone: lead.phone || "", email: lead.email || "",
    address: lead.address || "", district: lead.district || "", instagram: lead.instagram || "",
    source: lead.source || "sonstiges", website_state: lead.website_state || "unbekannt",
    priority: lead.priority || "mittel", problem: lead.problem || "", pitch: lead.pitch || "",
    notes: lead.notes || "", potential_value: lead.potential_value ?? "",
  });
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) { toast.error("Firmenname ist erforderlich"); return; }
    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}`, { ...form, potential_value: parseFloat(form.potential_value) || 0 });
      toast.success("Lead aktualisiert");
      qc.invalidateQueries();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-lead-dialog">
        <DialogHeader>
          <DialogTitle>Lead bearbeiten</DialogTitle>
          <DialogDescription>Stammdaten des Leads aktualisieren.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Firmenname *</Label>
              <Input data-testid="edit-company" value={form.company} onChange={(e) => set("company", e.target.value)} />
            </div>
            <div><Label>Branche</Label><Input value={form.industry} onChange={(e) => set("industry", e.target.value)} /></div>
            <div><Label>Website</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} /></div>
            <div><Label>Ansprechpartner</Label><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></div>
            <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Label>E-Mail</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label>Auftragswert (€)</Label><Input type="number" value={form.potential_value} onChange={(e) => set("potential_value", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div><Label>Bezirk</Label><Input data-testid="edit-district" value={form.district} onChange={(e) => set("district", e.target.value)} /></div>
            <div><Label>Instagram</Label><Input data-testid="edit-instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} /></div>
            <div>
              <Label>Priorität</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="edit-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quelle</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Website-Zustand</Label>
              <Select value={form.website_state} onValueChange={(v) => set("website_state", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{WEBSITE_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Problem</Label><Textarea rows={2} value={form.problem} onChange={(e) => set("problem", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Individueller Gesprächseinstieg</Label><Textarea data-testid="edit-pitch" rows={2} value={form.pitch} onChange={(e) => set("pitch", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Notizen</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" data-testid="edit-lead-submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
